import React, { useEffect, useState } from "react";
import "./Employee.css";
import { AiOutlineUserAdd, AiOutlineSearch } from "react-icons/ai";
import { getEmployees, updateEmployeeStatus } from "../../services/employeeService";
import { getAllUsers } from "../../services/userService"; // ✅ Import User Service
import Popup from "../../components/ui/Popup";
import EmployeeRegistration from "./EmployeeRegistration";
import EmployeeDetails from "./EmployeeDetails";
import RoleCreationPopup from "./RoleCreationPopup";
import UserProfilePopup from "./UserProfilePopup";

function EmployeeList() {
    const [employees, setEmployees] = useState([]);
    const [users, setUsers] = useState([]); // ✅ Store users with profiles
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [isRegisterOpen, setIsRegisterOpen] = useState(false);
    const rowsPerPage = 5;
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [isRolePopupOpen, setIsRolePopupOpen] = useState(false);
    const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);

    useEffect(() => {
        fetchEmployees();
        fetchUsers(); // ✅ Fetch users on component mount
    }, []);

    const fetchEmployees = async () => {
        try {
            const data = await getEmployees();
            if (!Array.isArray(data)) throw new Error("Invalid employee data");
            const sortedEmployees = [...data].sort((a, b) => new Date(b.joinedDate) - new Date(a.joinedDate));
            setEmployees(sortedEmployees);
        } catch (error) {
            console.error("Error fetching employees:", error);
        }
    };

    const fetchUsers = async () => {
        try {
            const data = await getAllUsers();
            setUsers(data);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const checkUserProfile = (employee) => {
        return users.find(
            (user) =>
                user.FirstName === employee.FirstName && user.LastName === employee.LastName
        );
    };

    const openEmployeeRegistration = () => {
        setIsRegisterOpen(true);
    };

    const closeEmployeeRegistration = () => {
        setIsRegisterOpen(false);
        fetchEmployees();
    };

    const handleStatusChange = async (nic, newStatus) => {
        try {
            await updateEmployeeStatus(nic, newStatus);
            setEmployees(employees.map(emp => (emp.NIC === nic ? { ...emp, status_id: parseInt(newStatus) } : emp)));

            setPopupMessage("Employee status updated successfully! ✅");
            setShowPopup(true);
            setTimeout(() => setShowPopup(false), 2000);
        } catch (error) {
            console.error("Error updating status:", error);
        }
    };

    const filteredEmployees = employees.filter((employee) =>
        Object.values(employee).some(value =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const totalPages = Math.ceil(filteredEmployees.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedEmployees = filteredEmployees.slice(startIndex, startIndex + rowsPerPage);

    return (
        <div className="content-wrapper">
            <div className="employee-list-container">
                <div className="header-actions">
                    <div className="search-bar">
                        <AiOutlineSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search employees..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                    <div className="button-group">
                        <button className="add-role-btn" onClick={() => setIsRolePopupOpen(true)}>
                            <AiOutlineUserAdd /> Add Role
                        </button>
                        <button className="add-employee-btn" onClick={openEmployeeRegistration}>
                            <AiOutlineUserAdd /> Add Employee
                        </button>
                    </div>
                </div>

                <div className="table-wrapper">
                    <table>
                        <thead>
                        <tr>
                            <th>NIC</th>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>DOB</th>
                            <th>Gender</th>
                            <th>Position</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {paginatedEmployees.length > 0 ? (
                            paginatedEmployees.map((employee) => {
                                const userProfile = checkUserProfile(employee);
                                return (
                                    <tr
                                        key={employee.NIC}
                                        onClick={() => setSelectedEmployee(employee)}
                                        className="clickable-row"
                                    >
                                        <td>{employee.NIC}</td>
                                        <td>{employee.FirstName}</td>
                                        <td>{employee.LastName}</td>
                                        <td>{new Date(employee.DOB).toISOString().split("T")[0]}</td>
                                        <td>{employee.Gender}</td>
                                        <td>{employee.Position || "Not Assigned"}</td>
                                        <td>{userProfile ? userProfile.role_name : "Not Assigned"}</td>
                                        <td>{userProfile ? userProfile.status_name : "Inactive"}</td>
                                        <td>
                                            {userProfile ? (
                                                <span className="profile-created">Profile Created</span>
                                            ) : (
                                                <button
                                                    className="profile-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Stop the event from bubbling up to the row
                                                        setSelectedEmployee(employee);
                                                        setIsProfilePopupOpen(true);
                                                    }}
                                                >
                                                    Create Profile
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="9" className="no-data">No employees found.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="pagination">
                        <button onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                            Prev
                        </button>
                        <span>Page {currentPage} of {totalPages}</span>
                        <button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
                            Next
                        </button>
                    </div>
                )}

                {selectedEmployee && (
                    <EmployeeDetails
                        isOpen={!!selectedEmployee}
                        employee={selectedEmployee}
                        onClose={() => setSelectedEmployee(null)}
                    />
                )}

                {isRolePopupOpen && (
                    <RoleCreationPopup
                        isOpen={isRolePopupOpen}
                        onClose={() => setIsRolePopupOpen(false)}
                        onRoleAdded={fetchEmployees}
                    />
                )}

                {isProfilePopupOpen && (
                    <UserProfilePopup
                        isOpen={isProfilePopupOpen}
                        onClose={() => setIsProfilePopupOpen(false)}
                        onProfileCreated={fetchEmployees}
                        selectedEmployee={selectedEmployee}
                    />
                )}

                {showPopup && <Popup message={popupMessage} onClose={() => setShowPopup(false)} />}
                {isRegisterOpen && <EmployeeRegistration isOpen={isRegisterOpen} onClose={closeEmployeeRegistration} onEmployeeAdded={fetchEmployees} />}
            </div>
        </div>
    );
}

export default EmployeeList;
